import {
  Component,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  resolveForwardRef,
  ValueProvider,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ClearFormDataActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { SelectWidget } from '@app/finxone-web-frontend/app/lib/actions/grid.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { PasswordService } from '@app/finxone-web-frontend/app/lib/services/password-service/password-service.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import {
  dispatchNestedFormValue,
  NestedFormDispatchMeta,
} from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.utils';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { restrictedFieldsToPurge } from '@app/finxone-web-frontend/app/lib/utils/form-widget-action-utils/form-tracking.utils';
import { getNestedValue } from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import { getComponentByLabel } from '@app/finxone-web-frontend/app/lib/utils/get-component.utils';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  DatePickerTypes,
  FormFieldConfig,
  FormsConfigType,
  InputFormatType,
  NewUiZoneWidgetConfig,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  cssToNgStyle,
  is2dArray,
  isLayoutWidget,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, debounceTime, map, takeUntil } from 'rxjs';
import { createChildWidgetInjector } from '../../../../zones/utils';
import { ChildComponentList } from '../../../../zones/widget-layout/main-widget/main-widget.component';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import {
  PARENT_FORM_ROW_GAP,
  RESOLVED_FORM_GROUP_PATH,
} from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-form-widget',
  template: `
    <div class="main-wrapper">
      <form class="flex flex-col finxForm" [formGroup]="formGroup" [ngStyle]="getFormStyles()">
        <ng-container *ngFor="let component of injectorArray">
          @if (configService.getActiveConfigType().isWorkflow) {
            <div
              [ngClass]="component.widget.uniqueId === mouseOverId ? mouseOver : ''"
              (click)="selectWidget($event, component.widget)"
              (mouseover)="changeStyle($event, component.widget.uniqueId.toString())"
              (mouseout)="changeStyle($event)"
            >
              <div [class]="getWidgetClasses(component.widget)">
                <ng-container
                  *ngComponentOutlet="component.component; injector: component.injector"
                ></ng-container>
              </div>
            </div>
          } @else {
            <div [class]="getWidgetClasses(component.widget)">
              <ng-container
                *ngComponentOutlet="component.component; injector: component.injector"
              ></ng-container>
            </div>
          }
        </ng-container>
      </form>
    </div>
  `,
})
export class FormWidgetComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  injectorComponents: {
    [key: string]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component: any;
      injector: Injector;
      widget: NewUiZoneWidgetConfig;
    };
  } = {};

  public formWidgetConfig: FormsConfigType;
  private isWorkflowType: boolean;
  private formType: 'root' | 'group' | 'array' = 'root';
  private nestedChildKey?: string;
  private nestedArrayIndex?: number;
  private nestedFormMeta: NestedFormDispatchMeta;

  get injectorArray() {
    return Object.values(this.injectorComponents);
  }

  public mouseOver = '';
  public mouseOverId = '';

  public formGroup: FormGroup;

  public childComponents = inject(ChildComponentList);
  private dataIn = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);
  private injector = inject(Injector);
  public configService = inject(ConfigService);
  private store = inject(Store);
  private workflowFormsService = inject(WorkflowFormsService);
  private zonePageUrlProvider = inject(ZonePageUrlProvider);
  private passwordService = inject(PasswordService);

  getWidgetClasses = getWidgetClasses;

  public get outerCss() {
    return cssToNgStyle(this.dataIn.widgetConfig.attributes.outerCss ?? '');
  }

  public getFormStyles(): Record<string, string> {
    const rowGap = this.formWidgetConfig?.rowGap;
    const legacyGap = typeof rowGap === 'number' ? `${rowGap}px` : rowGap;
    return {
      ...(this.outerCss as Record<string, string>),
      ...(legacyGap ? { gap: legacyGap } : {}),
    };
  }

  public getLegacyRowGap() {
    // for older configs that used value with no unit and hardcoded px
    return this.formWidgetConfig?.rowGap;
  }

  ngOnInit(): void {
    this.formWidgetConfig = this.dataIn.attri.widgetProperties?.['formsConfig'] ?? ({} as FormsConfigType);
    if (is2dArray(this.childComponents.list)) {
      throw new Error('Form widget cannot have multiple child component arrays');
    } else {
      const list = this.childComponents.list as NewUiZoneWidgetConfig[];

      // Resolve which FormGroup this widget's fields belong to BEFORE building child injectors:
      // leaf widgets like InputFieldWidgetComponent/AddressWidgetComponent don't inherit the
      // ambient [formGroup] from this component's own template - they independently resolve their
      // own target FormGroup from inputFieldConfig.parentForm, which only ever computes a plain
      // page-scoped path. For 'group'/'array' forms they need the resolved nested path handed to
      // them via RESOLVED_FORM_GROUP_PATH instead (see buildExtraProviders).
      const resolvedPath = this.resolveFormGroupTarget();
      this.nestedFormMeta = this.buildNestedFormMeta();

      const formInputsConfig: FormFieldConfig[] = [];
      list.forEach((widget) => {
        const component = resolveForwardRef(getComponentByLabel(widget.name));
        this.injectorComponents[widget.uniqueId] = {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          component: component,
          injector: createChildWidgetInjector(
            widget,
            this.injector,
            this.dataIn,
            this.buildExtraProviders(widget, resolvedPath),
          ),
          widget: widget,
        };

        this.addDynamicInputConfigs(formInputsConfig, widget);
      });
      this.isWorkflowType = this.configService.getActiveConfigType()?.isWorkflow;

      const formId = this.finalizeFormGroup(resolvedPath, formInputsConfig);

      this.workflowFormsService.registerCurrentPageFormWidgetForm(formId, this.nestedFormMeta);
      this.initFormValueListener();
    }
    this.clearFormData();
  }

  /**
   * Determines formType/parentFormID/childKey and returns the fully-resolved WorkflowFormsService
   * path this widget's fields will live at, ensuring any needed intermediate FormGroup/FormArray
   * containers exist. Does NOT add the actual field controls yet (formInputsConfig isn't built
   * until child widgets are iterated) - see finalizeFormGroup.
   */
  private resolveFormGroupTarget(): string {
    this.formType = this.formWidgetConfig?.formType ?? 'root';
    this.nestedChildKey = this.formWidgetConfig?.childKey;

    if (this.formType === 'group' && this.nestedChildKey) {
      const parentFormPath = this.workflowFormsService.generateCustomFormId(
        this.formWidgetConfig?.formID,
        this.zonePageUrlProvider,
      );
      let parentForm = this.workflowFormsService.getFormPart(parentFormPath);
      if (!parentForm) {
        // registerForCurrentPage: false - this is just the intermediate container, not the
        // widget's own submittable path; registering it would leave an orphan entry with no
        // nesting metadata that the Form Button would dispatch flat onto its full path.
        this.workflowFormsService.createForm(parentFormPath, [], false, false);
        parentForm = this.workflowFormsService.getFormPart(parentFormPath) as FormGroup;
      }
      // nestedChildKey may be a dot-path (e.g. "companyDetails.addressDetails") for
      // arbitrary-depth nesting - walk/create a FormGroup per segment, mirroring
      // resolveArrayItemFormPath's segment walk in WorkflowFormsService.
      let currentGroup = parentForm;
      this.nestedChildKey.split('.').forEach((segment) => {
        if (!currentGroup.contains(segment)) {
          currentGroup.addControl(segment, new FormGroup({}));
        }
        currentGroup = currentGroup.get(segment) as FormGroup;
      });
      return `${parentFormPath}.${this.nestedChildKey}`;
    }

    if (this.formType === 'array' && this.nestedChildKey) {
      const resolved = this.workflowFormsService.resolveArrayItemFormPath(
        this.formWidgetConfig?.formID,
        this.nestedChildKey,
        this.zonePageUrlProvider,
      );
      this.nestedArrayIndex = resolved.index;
      return resolved.path;
    }

    return (
      this.workflowFormsService.generateCustomFormId(
        this.formWidgetConfig?.formID,
        this.zonePageUrlProvider,
      ) ?? 'forms.form'
    );
  }

  /**
   * Captures where this widget's value nests in FormActionState, from the same formType/
   * parentFormID/childKey/arrayIndex resolveFormGroupTarget just set - used for both the
   * per-keystroke dispatch below and (via registerCurrentPageFormWidgetForm) the Form Button's
   * on-submit dispatch, so neither has to re-infer nesting from a path string's shape.
   */
  private buildNestedFormMeta(): NestedFormDispatchMeta {
    if (this.formType === 'group' && this.nestedChildKey) {
      return { rootFormId: this.formWidgetConfig?.formID, childKey: this.nestedChildKey };
    }

    if (this.formType === 'array' && this.nestedChildKey && this.nestedArrayIndex !== undefined) {
      return {
        rootFormId: this.formWidgetConfig?.formID,
        childKey: this.nestedChildKey,
        arrayIndex: this.nestedArrayIndex,
      };
    }

    return { rootFormId: this.formWidgetConfig?.formID ?? 'forms.form' };
  }

  /**
   * Adds this widget's field controls onto the path resolved by resolveFormGroupTarget, hydrates
   * from any previously-saved state, and sets this.formGroup. Returns the resolved path (used for
   * current-page validity tracking).
   */
  private finalizeFormGroup(resolvedPath: string, formInputsConfig: FormFieldConfig[]): string {
    if (this.formType === 'group' && this.nestedChildKey) {
      const childGroup = this.workflowFormsService.getFormPart(resolvedPath) as FormGroup;
      this.workflowFormsService.addFormControl(childGroup, formInputsConfig);
      this.workflowFormsService.trackFormValidity(resolvedPath, childGroup);
      this.formGroup = childGroup;

      const prevFormStateValue = getNestedValue(
        this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formWidgetConfig?.formID))
          ?.formData,
        this.nestedChildKey,
      );
      if (prevFormStateValue) {
        this.formGroup.patchValue(prevFormStateValue, { emitEvent: false });
      }

      return resolvedPath;
    }

    if (this.formType === 'array' && this.nestedChildKey && this.nestedArrayIndex !== undefined) {
      const itemGroup = this.workflowFormsService.getFormPart(resolvedPath) as FormGroup;
      this.workflowFormsService.addFormControl(itemGroup, formInputsConfig);
      this.workflowFormsService.trackFormValidity(resolvedPath, itemGroup);
      this.formGroup = itemGroup;

      const prevFormStateValue = getNestedValue(
        this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formWidgetConfig?.formID))
          ?.formData,
        `${this.nestedChildKey}.${this.nestedArrayIndex}`,
      );
      if (prevFormStateValue) {
        this.formGroup.patchValue(prevFormStateValue, { emitEvent: false });
      }

      return resolvedPath;
    }

    this.workflowFormsService.createForm(resolvedPath, formInputsConfig);

    const prevFormStateValue = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(this.formWidgetConfig?.formID ?? 'forms.form'),
    )?.formData;

    if (prevFormStateValue) {
      this.workflowFormsService.patchFormValues(resolvedPath, prevFormStateValue);
    }

    this.formGroup = this.workflowFormsService.getFormPart(resolvedPath) ?? new FormGroup({});

    return resolvedPath;
  }

  /**
   * Builds extra providers for child widget injectors.
   * Passes the parent form's rowGap to nested forms via PARENT_FORM_ROW_GAP token, and - only for
   * 'group'/'array' forms - the resolved target path via RESOLVED_FORM_GROUP_PATH so leaf widgets
   * that self-resolve their FormGroup (InputFieldWidgetComponent, AddressWidgetComponent) bind to
   * the same nested group/array-item this widget added its controls to, instead of recomputing a
   * plain page-scoped path from inputFieldConfig.parentForm.
   */
  private buildExtraProviders(widget: NewUiZoneWidgetConfig, resolvedFormGroupPath: string) {
    const providers: (
      ValueProvider | { provide: typeof ChildComponentList; useValue: ChildComponentList }
    )[] = [];

    // Always pass rowGap to child widgets (they can choose to use it or not)
    providers.push({
      provide: PARENT_FORM_ROW_GAP,
      useValue: this.formWidgetConfig?.rowGap,
    });

    if (this.formType !== 'root') {
      providers.push({
        provide: RESOLVED_FORM_GROUP_PATH,
        useValue: resolvedFormGroupPath,
      });
    }

    // For layout widgets, also provide ChildComponentList
    if (isLayoutWidget(widget.name)) {
      providers.push({
        provide: ChildComponentList,
        useValue: new ChildComponentList(widget.childComponents ?? []),
      });
    }

    return providers;
  }

  private clearFormData() {
    // clearFormData only applies to root-typed forms - 'group'/'array' forms nest inside a
    // shared parent form that other pages/widgets also write into, so wholesale reset/clear
    // isn't a safe operation to expose there.
    if (this.formType !== 'root') return;
    if (this.formWidgetConfig?.clearFormData) {
      this.workflowFormsService.resetForm(this.formWidgetConfig?.formID);
      this.store.dispatch(new ClearFormDataActionWithId(this.formWidgetConfig?.formID));
    }
  }

  /**
   * Dynamically appends additional form input configurations
   * based on widget input type (date, phone, or email).
   *
   * @param formInputsConfig The current list of form field configurations.
   * @param widget The widget containing input field configuration properties.
   */
  private addDynamicInputConfigs(formInputsConfig: FormFieldConfig[], widget: NewUiZoneWidgetConfig): void {
    const inputFieldConfig = widget.attributes?.widgetProperties?.inputFieldConfig;
    if (!inputFieldConfig) {
      // Recursively process child components for layout widgets
      // in case they contain input fields
      if (widget.childComponents) {
        if (Array.isArray(widget.childComponents[0])) {
          // Handle 2D array of child components
          for (const childArray of widget.childComponents as NewUiZoneWidgetConfig[][]) {
            childArray.forEach((childWidget) => {
              this.addDynamicInputConfigs(formInputsConfig, childWidget);
            });
          }
        } else {
          (widget.childComponents as NewUiZoneWidgetConfig[]).forEach((childWidget) => {
            this.addDynamicInputConfigs(formInputsConfig, childWidget);
          });
        }
      }
      return;
    }

    // Always push the base config first
    formInputsConfig.push(inputFieldConfig);

    const { dateFormat, inputFormat, phoneNumberConfig } = inputFieldConfig;

    // Handle Date input (day/month/year)
    if (dateFormat === DatePickerTypes.INPUT) {
      this.createInputFieldConfig(['day', 'month', 'year'], inputFieldConfig, formInputsConfig);
      return;
    }

    // Handle Phone number input
    if (inputFormat === InputFormatType.PHONE_NUMBER) {
      if (!phoneNumberConfig?.attachToInput) {
        const configWithDefault = { ...inputFieldConfig, defaultValue: '+44' };
        this.createInputFieldConfig(['countryCode'], configWithDefault, formInputsConfig, {});
      }
    }
  }

  private createInputFieldConfig(
    controlNames: string[],
    inputFieldConfig: FormFieldConfig,
    formInputsConfig: FormFieldConfig[],
    validation?: FormFieldConfig['validation'],
  ) {
    controlNames.forEach((controlName) => {
      formInputsConfig.push({
        ...inputFieldConfig,
        validation: validation ?? { ...inputFieldConfig?.validation },
        name: (inputFieldConfig?.[controlName] || controlName) as string,
      });
    });
  }

  public changeStyle($event: any, mouseOverId?: string) {
    this.mouseOver = $event.type == 'mouseover' ? 'widget-hover' : '';
    if (mouseOverId) this.mouseOverId = mouseOverId;
    $event.stopPropagation();
  }

  public selectWidget(event: MouseEvent, widget: NewUiZoneWidgetConfig) {
    if (this.isWorkflowType) {
      if (event.shiftKey) {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), true));
      } else {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), false));
      }
    }
    event.stopPropagation();
  }

  private initFormValueListener() {
    this.formGroup.valueChanges
      ?.pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        map((formValue) => {
          restrictedFieldsToPurge.forEach((field) => {
            // store password temporarily BEFORE deletion
            if (formValue?.[field]) {
              this.passwordService.set(field, formValue[field]);
            }
            // remove sensitive fields from payload
            delete formValue?.[field];
          });

          this.dispatchFormValue(formValue);
        }),
      )
      .subscribe();
  }

  /**
   * FormActionState's SetFormActionWithId reducer only shallow-merges at formData's top level,
   * so a 'group'/'array' form's value must be nested via nestedFormMeta rather than written
   * straight through - see dispatchNestedFormValue.
   */
  private dispatchFormValue(formValue: Record<string, unknown>): void {
    dispatchNestedFormValue(this.store, this.nestedFormMeta, formValue);
  }

  ngOnDestroy() {
    this.workflowFormsService.clearCurrentPageForms();
  }
}
